import React, { useCallback, useEffect, useState } from "react";
import { auth } from "../../App";
import { formatResendCooldown } from "./PortalEmailVerificationPanel";
import {
  EXISTING_ACCOUNT_VERIFICATION_BANNER_COPY,
  shouldShowExistingAccountEmailVerificationBanner,
} from "../../utils/portalExistingAccountEmailVerificationModel";
import { mapEmailVerificationSendError } from "../../utils/portalEmailVerificationModel";
import { readVerificationResendCooldownAt } from "../../utils/portalEmailVerificationSendGuard";
import { sendPortalVerificationEmailGuarded } from "../../utils/clientPortalInviteLifecycleController";
import { sendPortalVerificationEmail } from "../../services/sendPortalVerificationEmailCallable";

export default function PortalExistingAccountEmailVerificationBanner({ user }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [emailSent, setEmailSent] = useState(() => readVerificationResendCooldownAt() > 0);
  const [verificationLastSentAt, setVerificationLastSentAt] = useState(
    () => readVerificationResendCooldownAt() || 0
  );
  const [cooldownMs, setCooldownMs] = useState(() => formatResendCooldown(verificationLastSentAt));

  const visible = shouldShowExistingAccountEmailVerificationBanner(user);

  useEffect(() => {
    if (!visible) return;
    const remaining = formatResendCooldown(verificationLastSentAt);
    setCooldownMs(remaining);
    if (remaining <= 0) return undefined;
    const timerId = window.setInterval(() => {
      setCooldownMs((prev) => (prev <= 1000 ? 0 : prev - 1000));
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [visible, verificationLastSentAt]);

  const sendVerificationEmailOnce = useCallback(async () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const sendResult = await sendPortalVerificationEmailGuarded({
      sendFn: async () => {
        await sendPortalVerificationEmail({ continueUrl: origin });
      },
    });
    if (!sendResult.sent) {
      if (sendResult.error) throw sendResult.error;
      return sendResult;
    }
    const sentAt = sendResult.sentAt || readVerificationResendCooldownAt();
    setVerificationLastSentAt(sentAt);
    setEmailSent(true);
    return sendResult;
  }, []);

  const handleSendOrResend = async () => {
    if (busy || cooldownMs > 0 || !auth.currentUser) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const sendResult = await sendVerificationEmailOnce();
      if (sendResult?.sent) {
        setNotice(EXISTING_ACCOUNT_VERIFICATION_BANNER_COPY.sendSuccess);
      }
    } catch (sendErr) {
      setError(mapEmailVerificationSendError(sendErr));
    } finally {
      setBusy(false);
    }
  };

  const handleVerifiedRefresh = async () => {
    if (busy || !auth.currentUser) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await auth.currentUser.reload();
      const refreshed = auth.currentUser;
      if (refreshed.emailVerified !== true) {
        setNotice(EXISTING_ACCOUNT_VERIFICATION_BANNER_COPY.notVerifiedYet);
        return;
      }
      await refreshed.getIdToken(true);
      setNotice(EXISTING_ACCOUNT_VERIFICATION_BANNER_COPY.verifiedSuccess);
    } catch (verifyErr) {
      console.error("Existing-account verification refresh failed:", verifyErr);
      setError("We could not confirm your email verification. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;

  const resendDisabled = busy || cooldownMs > 0;
  const sendLabel =
    cooldownMs > 0
      ? `Resend available in ${Math.ceil(cooldownMs / 1000)}s`
      : emailSent
        ? "Resend verification email"
        : "Send verification email";

  return (
    <div
      className="border-b border-amber-200 bg-amber-50 px-4 py-4 md:px-6"
      data-testid="existing-account-email-verification-banner"
      role="region"
      aria-label={EXISTING_ACCOUNT_VERIFICATION_BANNER_COPY.title}
    >
      <div className="mx-auto flex max-w-7xl flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-950">{EXISTING_ACCOUNT_VERIFICATION_BANNER_COPY.title}</p>
          <p className="mt-1 text-sm text-amber-900">
            {EXISTING_ACCOUNT_VERIFICATION_BANNER_COPY.lead}
            {user?.email ? (
              <>
                {" "}
                Account: <span className="font-medium">{user.email}</span>.
              </>
            ) : null}
          </p>
          <p className="mt-2 text-sm text-amber-900">{EXISTING_ACCOUNT_VERIFICATION_BANNER_COPY.expiredLink}</p>
          {error ? (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>
          ) : null}
          {notice ? (
            <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-950">
              {notice}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap lg:justify-end">
          <button
            type="button"
            className="min-h-10 rounded-lg border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-950 hover:bg-amber-100 disabled:opacity-50"
            onClick={handleSendOrResend}
            disabled={resendDisabled}
            data-testid="existing-account-send-verification-email"
          >
            {sendLabel}
          </button>
          <button
            type="button"
            className="min-h-10 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50"
            onClick={handleVerifiedRefresh}
            disabled={busy}
            data-testid="existing-account-refresh-verification"
          >
            {busy ? "Checking…" : "I've verified my email"}
          </button>
        </div>
      </div>
    </div>
  );
}
